import React from 'react';
import { HouseLine, Sparkle, User, ArrowRight } from '@phosphor-icons/react';
export interface LandlordRentalEntry {
  id: string;
  tenantName: string;
  tenantAvatar?: string;
  listingTitle?: string;
  // ISO string. When the rental was confirmed (succesAt).
  closedAt: string;
  // Optional pretty period label, e.g. "Mai 2024 – Apr 2026".
  periodLabel?: string;
}
interface LandlordRentalHistoryProps {
  entries: LandlordRentalEntry[];
  // Tweaks copy and empty-state when the viewer is the landlord themselves.
  isOwner?: boolean;
  // Affects empty-state wording ("agent" vs "proprietar").
  subRole?: 'proprietar' | 'agent' | null;
  // Optional callback to navigate to archive
  onViewArchive?: () => void;
}
function formatClosedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('ro-RO', {
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return '';
  }
}
export function LandlordRentalHistory({
  entries,
  isOwner = false,
  subRole,
  onViewArchive
}: LandlordRentalHistoryProps & {
  onViewArchive?: () => void;
}) {
  const count = entries.length;
  return <section className="rounded-2xl bg-dark-2 border border-dark-3 px-6 py-7" data-id="element-447">
      <div className="flex items-center justify-between mb-1" data-id="element-448">
        <h3 className="text-base font-semibold text-light-1" data-id="element-449">
          Istoric închirieri
        </h3>
        {count > 0 && <span className="text-xs text-light-3" data-id="element-450">
            {count} {count === 1 ? 'închiriere' : 'închirieri'}
          </span>}
      </div>

      <p className="text-xs text-light-3 mb-4 leading-relaxed" data-id="element-451">
        {isOwner ? 'Chiriașii cărora le-ai închiriat — fiecare ofertă marcată ca succes devine o intrare aici.' : `Închirieri finalizate pe Izio prin ${subRole === 'agent' ? 'acest agent' : 'acest proprietar'}.`}
      </p>

      {count === 0 ? <div className="rounded-xl border border-dashed border-dark-4 p-5 text-center" data-id="element-452">
          <HouseLine size={32} weight="regular" className="text-light-4 mx-auto mb-2" data-id="element-453" />
          <p className="text-sm text-light-3 leading-relaxed" data-id="element-454">
            {isOwner ? 'Nu ai încă închirieri finalizate. Când un chiriaș marchează oferta ta ca succes, va apărea aici.' : `Acest ${subRole === 'agent' ? 'agent' : 'proprietar'} nu are încă închirieri finalizate pe Izio.`}
          </p>
        </div> : <ul className="space-y-2.5" data-id="element-455">
          {entries.map(entry => {
        const date = entry.periodLabel || formatClosedAt(entry.closedAt);
        return <li key={entry.id} className="rounded-xl bg-dark-1 border border-dark-3 p-3 flex items-center gap-3" data-id="element-456">
                {entry.tenantAvatar ? <img src={entry.tenantAvatar} alt={entry.tenantName} className="w-10 h-10 rounded-full object-cover shrink-0" data-id="element-457" /> : <div className="w-10 h-10 rounded-full bg-dark-3 inline-flex items-center justify-center shrink-0" data-id="element-458">
                    <User size={18} weight="regular" className="text-light-3" data-id="element-459" />
                  </div>}
                <div className="min-w-0 flex-1" data-id="element-460">
                  <p className="text-sm font-medium text-light-1 truncate" data-id="element-461">
                    {entry.tenantName}
                  </p>
                  <p className="text-xs text-light-3 truncate mt-0.5" data-id="element-462">
                    {entry.listingTitle ? <>
                        {entry.listingTitle}
                        {date && <>
                            <span className="text-light-4" data-id="element-463"> · </span>
                            {date}
                          </>}
                      </> : date}
                  </p>
                </div>
                {isOwner && onViewArchive && <button type="button" onClick={onViewArchive} className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-light-3 hover:text-light-1 transition-colors cursor-pointer" data-id="element-464">
                    Vezi în Arhivă <ArrowRight size={12} data-id="element-465" />
                  </button>}
              </li>;
      })}
        </ul>}
    </section>;
}